import React, { useState, useEffect } from 'react';
import { Layout, Menu, Modal, Button, Drawer, Dropdown, Badge } from 'antd';
import axios from 'axios';
import { apiUrl } from './config';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  EllipsisOutlined,
  UserOutlined,
  LogoutOutlined,
  HistoryOutlined,
  UserAddOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  SendOutlined,
  CalendarOutlined,
  ScheduleOutlined
} from '@ant-design/icons';
import { useUser } from '../context/UserContext';
import logo from '../images/logo.png'; // Assicurati che il percorso del logo sia corretto
import '../styles/Navbar.css';
import { isMobile, isIOS } from 'react-device-detect'; // Importa il rilevatore di dispositivo
import { Preferences } from '@capacitor/preferences';



const { Header, Footer } = Layout;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const { user, setUser } = useUser();

  const handleButtonClick = () => {
    if (isMobile) {
      if (isIOS) {
        // Prova ad aprire la pagina di ASD Tyrsenia Sporting Club nell'app su iOS
        window.location.href = 'https://sportclubby.app.link/iv51dzfaGZ';
        setTimeout(() => {
          // Se l'app non è installata, reindirizza all'App Store
          window.location.href = 'https://apps.apple.com/it/app/sportclubby/id1134713487';
        }, 2000); // Attendi 2 secondi per il fallback
      } else {
        // Prova ad aprire la pagina di ASD Tyrsenia Sporting Club nell'app su Android
        window.location.href = 'https://sportclubby.app.link/iv51dzfaGZ';
        setTimeout(() => {
          // Se l'app non è installata, reindirizza a Google Play
          window.location.href = 'https://play.google.com/store/apps/details?id=com.sportclubby.app';
        }, 2000); // Attendi 2 secondi per il fallback
      }
    } else {
      // Su desktop, reindirizza al sito web
      window.location.href = 'https://www.sportclubby.com/it/cervetereri/asd-tyrsenia-sporting-club';
    }
  };

  const handleLogout = async () => {
    setIsLogoutModalVisible(false);
    await Preferences.remove({ key: 'token' });
    await Preferences.remove({ key: 'user' });
    setUser(null);
    navigate('/login');
  };
  

  const showLogoutConfirm = () => {
    setIsDrawerVisible(false); // Chiudi il drawer
    setIsLogoutModalVisible(true); // Mostra il modal
  };

  const handleCancelLogout = () => {
    setIsLogoutModalVisible(false);
  };

  const toggleDrawer = () => {
    setIsDrawerVisible(!isDrawerVisible);
  };

  const closeDrawerAndNavigate = (path) => {
    setIsDrawerVisible(false);
    navigate(path);
  };

  const homePath = user
    ? user.role === 'trainer'
      ? '/trainer-dashboard'
      : '/cliente-dashboard'
    : '/';

  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/android' ||
    location.pathname === '/register' ||
    location.pathname === '/' ||
    location.pathname === '/change-password' ||
    location.pathname.includes('/reset-password');

  // Numero di richieste di registrazione in attesa. Senza questo non c'e' modo
  // di sapere che ne e' arrivata una: e' il motivo per cui alcune sono rimaste
  // ferme per mesi.
  const [richiesteInAttesa, setRichiesteInAttesa] = useState(0);

  useEffect(() => {
    if (user?.role !== 'trainer') return undefined;

    let annullato = false;

    const conta = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/api/auth/register-requests`);
        if (!annullato) setRichiesteInAttesa(Array.isArray(data) ? data.length : 0);
      } catch (errore) {
        // Il conteggio e' un di piu': se non arriva, il menu resta senza numero.
      }
    };

    conta();
    // Riaggiorna quando il trainer cambia schermata, cosi' il numero cala
    // subito dopo che ha approvato una richiesta.
    return () => { annullato = true; };
  }, [user, location.pathname]);

  const trainerMenu = (
    <Menu style={{ fontSize: '16px' }}>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="register-user"
        icon={<UserAddOutlined />}
        onClick={() => closeDrawerAndNavigate('/register-user')}
      >
        Registra Utente
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="exercise-list"
        icon={<UnorderedListOutlined />}
        onClick={() => closeDrawerAndNavigate('/exercise-list')}
      >
        Lista Esercizi
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="plan-corsi"
        icon={<CalendarOutlined />}
        onClick={() => closeDrawerAndNavigate('/plan-corsi')}
      >
        Plan Corsi
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="history"
        icon={<HistoryOutlined />}
        onClick={() => closeDrawerAndNavigate('/trainer-history')}
      >
        Storico Richieste
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="client-list"
        icon={<TeamOutlined />}
        onClick={() => closeDrawerAndNavigate('/client-list')}
      >
        Lista Utenti
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="profile"
        icon={<UserOutlined />}
        onClick={() => closeDrawerAndNavigate('/profile')}
      >
        Profilo
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="sportclubby"
        icon={<ScheduleOutlined />}
        onClick={handleButtonClick}
      >
        Prenota Lezione
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="trainers"
        icon={<TeamOutlined />}
        onClick={() => closeDrawerAndNavigate('/personal-trainers')}
      >
        Personal Trainer
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="registrations"
        icon={<UserAddOutlined />}
        onClick={() => closeDrawerAndNavigate('/registrations')}
      >
        Registrazioni
        {richiesteInAttesa > 0 && (
          <Badge
            count={richiesteInAttesa}
            style={{ backgroundColor: 'var(--primario)', marginLeft: 10 }}
          />
        )}
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="logout"
        icon={<LogoutOutlined />}
        onClick={showLogoutConfirm}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  const clientMenu = (
    <Menu>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="exercise-list"
        icon={<UnorderedListOutlined />}
        onClick={() => closeDrawerAndNavigate('/exercise-list')}
      >
        Lista Esercizi
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="plan-corsi"
        icon={<CalendarOutlined />}
        onClick={() => closeDrawerAndNavigate('/plan-corsi')}
      >
        Plan Corsi
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="sportclubby"
        icon={<ScheduleOutlined />}
        onClick={handleButtonClick}
      >
        Prenota Lezione
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="trainers"
        icon={<TeamOutlined />}
        onClick={() => closeDrawerAndNavigate('/personal-trainers')}
      >
        Personal Trainer
      </Menu.Item>
      <Menu.Item
        style={{ marginTop: '20px' }}
        key="logout"
        icon={<LogoutOutlined />}
        onClick={showLogoutConfirm}
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  const trainerDropdownMenu = (
    <Menu>
      <Menu.Item key="register-user" icon={<UserAddOutlined />} onClick={() => navigate('/register-user')}>
        Registra Utente
      </Menu.Item>
      <Menu.Item key="registrations" icon={<UserAddOutlined />} onClick={() => navigate('/registrations')}>
        Richieste Registrazione
      </Menu.Item>
      <Menu.Item key="trainer-history" icon={<HistoryOutlined />} onClick={() => navigate('/trainer-history')}>
        Storico Richieste
      </Menu.Item>
      <Menu.Item key="exercise-list" icon={<UnorderedListOutlined />} onClick={() => navigate('/exercise-list')}>
        Lista Esercizi
      </Menu.Item>
      <Menu.Item key="client-list" icon={<TeamOutlined />} onClick={() => navigate('/client-listlist')}>
        Lista Esercizi
      </Menu.Item>
      <Menu.Item key="personal-trainers" icon={<TeamOutlined />} onClick={() => navigate('/personal-trainers')}>
        Personal Trainer
      </Menu.Item>
      <Menu.Item key="sportclubby" icon={<ScheduleOutlined />} onClick={handleButtonClick}>
        Prenota Corsi
      </Menu.Item>
    </Menu>
  );

  if (isAuthPage) {
    return null;
  }

  return (
    <>
      <Header className="desktop-navbar">
        <div className="logo-container">
          <img src={logo} alt="Logo" className="logo" />
        </div>
        <div className="desktop-center-buttons">
          <Link to={homePath}>
            <Button type="link">Home</Button>
          </Link>
          {user?.role === 'trainer' && (
            <>
              <Link to="/create-workout">
              <Button type="link">Nuova Scheda</Button>
            </Link>
            <Link to="/workout-plans">
              <Button type="link">Schede</Button>
            </Link>
            </>
          )}
          {user?.role === 'cliente' && (
            <>
              <Link to="/new-scheda">
              <Button type="link">Richiedi Scheda</Button>
            </Link>
            </>
          )}
          <Link to="/exercise-list">
            <Button type="link">Lista Esercizi</Button>
          </Link>
          <Link to="/plan-corsi">
            <Button type="link">Plan Corsi</Button>
          </Link>
          <Link to="/daily-workout">
            <Button type="link">Daily Workout</Button>
          </Link>
          {user?.role === 'trainer' && (
            <>
            <Dropdown overlay={trainerDropdownMenu} placement="bottomLeft">
              <Button type="link" icon={<EllipsisOutlined />}>
                Menu
              </Button>
            </Dropdown>
            </>
          )}
        </div>
        <div className="desktop-right-buttons">
          <Button type="link" icon={<UserOutlined />} onClick={() => navigate('/profile')}>
            Profilo
          </Button>
          <Button type="link" icon={<LogoutOutlined />} onClick={showLogoutConfirm}>
            Logout
          </Button>
        </div>
      </Header>

      {/* Footer per Trainer */}
      {!isAuthPage && user?.role === 'trainer' && (
        <Footer className="mobile-navbar">
          <Link to={homePath}>
            <Button type="link" icon={<HomeOutlined />}>
              <span>Home</span>
            </Button>
          </Link>
          <Link to="/create-workout">
            <Button type="link" icon={<PlusCircleOutlined />}>
              <span>Nuova Scheda</span>
            </Button>
          </Link>
          <Link to="/workout-plans">
            <Button type="link" icon={<FileTextOutlined />}>
              <span>Schede</span>
            </Button>
          </Link>
          <Button type="link" icon={<EllipsisOutlined />} onClick={toggleDrawer}>
            <span>Menu</span>
          </Button>
        </Footer>
      )}

      {!isAuthPage && user?.role === 'cliente' && (
        <Footer className="mobile-navbar">
          <Link to={homePath}>
            <Button type="link" icon={<HomeOutlined />}>
              <span>Home</span>
            </Button>
          </Link>
          <Button type="link" icon={<SendOutlined />} onClick={() => navigate('/new-scheda')}>
            <span>Richiedi Scheda</span>
          </Button>
          <Button type="link" icon={<UserOutlined />} onClick={() => navigate('/profile')}>
            <span>Profilo</span>
          </Button>
          <Button type="link" icon={<EllipsisOutlined />} onClick={toggleDrawer}>
            <span>Menu</span>
          </Button>
        </Footer>
      )}


      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="Logo" style={{ height: '40px', marginRight: '10px' }} /> {/* Logo nel Drawer */}
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Fit&Lab</span>
          </div>
        }
        placement="right"
        onClose={toggleDrawer}
        open={isDrawerVisible}
        width={250}
      >
        {/* Contenuto del drawer */}
        {user?.role === 'trainer' ? trainerMenu : clientMenu}
      </Drawer>

      <Modal
        title="Conferma Logout"
        open={isLogoutModalVisible}
        onOk={handleLogout}
        onCancel={handleCancelLogout}
        okText="Logout"
        cancelText="Annulla"
        getContainer={document.body}
        className="custom-modal"
      >
        <p>Sei sicuro di voler effettuare il logout?</p>
      </Modal>
    </>
  );
};

export default Navbar;
